// Window interface extensions are declared in types/globals.d.ts

import { TREECHAT_DEVS } from 'constants/treechatDevs'
import { analyticsSessionId } from 'lib/analyticsSession'
import { isMobile } from 'lib/device'

const ATTRIBUTION_PARAM_KEYS = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_term',
    'utm_content',
    'gclid',
    'gbraid',
    'wbraid',
    'prospect_token',
    'landing_page',
    'landing_button',
    'landing_page_url',
    'landing_page_host',
    'landing_page_path',
    'landing_page_search',
    'landing_referrer',
    'amplitude_device_id',
]

// Event name validation - ensures consistent naming convention
const VALID_EVENT_PREFIXES = [
    'VIEW_',
    'CLICK_',
    'SUBMIT_',
    'CREATE_',
    'UPDATE_',
    'DELETE_',
    'SHARE_',
    'AUTH_',
    'BSV_',
    'ERROR_',
    'AI_',
    'UPLOAD_',
    'PLAY_',
    'OPEN_',
    'CLOSE_',
    'TOGGLE_',
    'CLEAR_',
    'SKIP_',
    // Legacy events without prefix (for backwards compatibility)
    'SEARCH',
    'STREAM VIEW',
    'QUEST VIEW',
    'LOAD SPACE',
    'POST_ROOT',
    'POST_ANSWER',
    'TWEET_QUEST',
    'ARCHIVE_ANSWER',
    'UNARCHIVE_ANSWER',
    'PULL_TO_REFRESH',
    'CLICK_CHROME_EXTENSION',
    'NAVIGATE_SOURCE',
]

function isValidEventName(event: string): boolean {
    // Check if it's a legacy event (exact match)
    const legacyEvents = VALID_EVENT_PREFIXES.filter(prefix => !prefix.endsWith('_'))
    if (legacyEvents.includes(event)) {
        return true
    }

    // Check if it starts with a valid prefix
    const prefixes = VALID_EVENT_PREFIXES.filter(prefix => prefix.endsWith('_'))
    return prefixes.some(prefix => event.startsWith(prefix))
}

// Determine user type for analytics segmentation
function getUserType(): 'dev' | 'admin' | 'regular' {
    const currentUser = window?.gon?.currentUser
    if (!currentUser) return 'regular'

    // Check if user is a developer
    if (TREECHAT_DEVS.includes(currentUser.username)) {
        return 'dev'
    }

    // Check if user is an admin
    if (currentUser.is_admin || currentUser.admin) {
        return 'admin'
    }

    return 'regular'
}

// Get current stream context
function getStreamContext(): Record<string, any> {
    const context: Record<string, any> = {}

    // Try to get the current panel filter from the URL or state
    const path = window.location.pathname

    if (path.includes('/stream/public')) {
        context.stream_type = 'public'
    } else if (path.includes('/stream/private')) {
        context.stream_type = 'private'
    } else if (path.includes('/stream/ordinals')) {
        context.stream_type = 'ordinals'
    } else if (path.includes('/stream/treechat')) {
        context.stream_type = 'treechat'
    } else if (path.match(/\/stream\/[a-f0-9-]+/)) {
        context.stream_type = 'team'
    } else if (path.includes('/quest/')) {
        context.stream_type = 'quest'
    }

    // Add authentication status
    context.is_authenticated = !!window?.gon?.currentUser

    return context
}

function getAttributionParamsFromUrl(): Record<string, string> {
    const attributionParams: Record<string, string> = {}
    if (typeof window === 'undefined' || !window.location) {
        return attributionParams
    }

    try {
        const searchParams = new URLSearchParams(window.location.search)

        ATTRIBUTION_PARAM_KEYS.forEach(key => {
            const value = searchParams.get(key)
            if (value) {
                attributionParams[key] = value
            }
        })
    } catch (err) {
        // Silently fail if URL parsing fails
    }

    return attributionParams
}

// Extract campaign parameters from URL. Kept for older callers/docs that use the UTM name.
export function getUtmParams(): Record<string, string> {
    const utmParams: Record<string, string> = {}
    const attributionParams = getAttributionParamsFromUrl()

    Object.entries(attributionParams).forEach(([key, value]) => {
        if (key.startsWith('utm_')) {
            utmParams[key] = value
        }
    })

    return utmParams
}

// Get default properties for all events
function getDefaultProperties(): Record<string, any> {
    const defaults: Record<string, any> = {}

    // User context - Only non-PII data
    // User identification is handled at Amplitude session level
    defaults.user_type = getUserType()

    // Stream context
    Object.assign(defaults, getStreamContext())

    // Space/subdomain info
    if (window?.gon?.space?.id) {
        defaults.space_id = window.gon.space.id
        defaults.space_subdomain = window.gon.space.subdomain
    }

    // Navigation context
    if (window.location) {
        defaults.current_host = window.location.hostname
        defaults.current_path = window.location.pathname
        defaults.current_search = window.location.search
        defaults.current_url = window.location.href
    }

    // Referrer tracking (where user came from)
    if (typeof document !== 'undefined' && document.referrer) {
        defaults.referrer = document.referrer
    }

    // Campaign/referrer attribution for funnel tracking.
    const attributionParams = getAttributionParamsFromUrl()
    if (Object.keys(attributionParams).length > 0) {
        Object.assign(defaults, attributionParams)
    }

    // Platform info
    defaults.platform = 'web'
    defaults.user_agent = navigator.userAgent
    defaults.is_mobile = isMobile

    // Timestamp
    defaults.timestamp = new Date().toISOString()

    // Session ID (generate if not exists). Shared with the engagement event log
    // so the two streams join on session_id.
    defaults.session_id = analyticsSessionId()

    return defaults
}

// Flag to control development analytics logging
let debugMode = false

export function logEv(event: string, props: Record<string, any> = {}): void {
    try {
        // Only send to Amplitude in production
        if (window?.gon?.env === 'production') {
            // Merge default properties with provided props
            const enrichedProps = {
                ...getDefaultProperties(),
                ...props,
            }

            // Send to Amplitude
            window.amplitude.getInstance().logEvent(event, enrichedProps)
        } else if (debugMode) {
            // Only log in development if debug mode is explicitly enabled

            // Validate event name
            if (!isValidEventName(event)) {
                console.warn(
                    `[Analytics] Invalid event name: "${event}". Should start with a valid prefix (VIEW_, CLICK_, etc.) or be a legacy event.`,
                )
            }

            // Merge default properties with provided props for logging
            const enrichedProps = {
                ...getDefaultProperties(),
                ...props,
            }

            console.group(`📊 [Analytics Event] ${event}`)
            console.log('Properties:', enrichedProps)
            console.groupEnd()
        }
        // Silent in development by default (no logging unless debug mode is on)
    } catch (err) {
        console.error('[Analytics Error]', err)
    }
}

// Development helper to see all tracked events
export function enableAnalyticsDebug(): void {
    if (window?.gon?.env !== 'production') {
        debugMode = true
        console.log('📊 Analytics Debug Mode Enabled - Call disableAnalyticsDebug() to turn off')
    }
}

// Development helper to disable analytics logging
export function disableAnalyticsDebug(): void {
    if (window?.gon?.env !== 'production') {
        debugMode = false
        console.log('📊 Analytics Debug Mode Disabled')
    }
}

// Make debug functions available globally in development
if (window && window?.gon?.env !== 'production') {
    window.enableAnalyticsDebug = enableAnalyticsDebug
    window.disableAnalyticsDebug = disableAnalyticsDebug
}

window.logEv = logEv

// Set persistent, non-PII user properties in Amplitude
export function setUserProps(props: Record<string, any>): void {
    try {
        if (window.gon && window.gon.currentUser && window?.gon?.env === 'production') {
            window.amplitude.getInstance().setUserProperties(props)
        } else if (debugMode) {
            console.group('📊 [Analytics UserProps]')
            console.log('Properties:', props)
            console.groupEnd()
        }
    } catch (err) {
        console.error('[Analytics UserProps Error]', err)
    }
}
